/**
 * AnalyseProgressBanner — visual pipeline stepper during PARSING.
 *
 * Polls /rfp/{id}/progress every 2 s while parse_status is PARSING;
 * stops at ANALYZED / FAILED. Calls onComplete so the caller can
 * refresh the rest of the page.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, CheckCircle2, AlertTriangle,
  FileText, LayoutList, Tag, Sparkles, AlignLeft, Search,
} from "lucide-react";
import { v2Api } from "@/lib/v2Api";

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATES  = new Set(["ANALYZED", "FAILED"]);

const PIPELINE = [
  { key: "parsing",    label: "Parse",    icon: FileText,   desc: "Extracting text from PDF"   },
  { key: "sectionize", label: "Sections", icon: LayoutList, desc: "Splitting into sections"     },
  { key: "classify",   label: "Classify", icon: Tag,        desc: "Categorising each section"   },
  { key: "extract",    label: "Extract",  icon: Sparkles,   desc: "Pulling key fields"          },
  { key: "summarise",  label: "Summarise",icon: AlignLeft,  desc: "Generating AI summaries"     },
  { key: "indexing",   label: "Index",    icon: Search,     desc: "Building search index"       },
];

const STEP_INDEX = Object.fromEntries(PIPELINE.map((s, i) => [s.key, i]));

export default function AnalyseProgressBanner({
  rfpId, parseStatus, onComplete, onPartial,
}) {
  const [snapshot,      setSnapshot]      = useState(null);
  const [elapsed,       setElapsed]       = useState(0);
  const [lastGoodStage, setLastGoodStage] = useState("parsing");
  const startTsRef     = useRef(null);
  const timerRef       = useRef(null);
  const elapsedRef     = useRef(null);
  const lastPartialRef = useRef(0);

  // Elapsed clock — prefer backend start timestamp for accuracy.
  const backendStartTs = snapshot?.progress?.started_at_ts;
  useEffect(() => {
    if (parseStatus !== "PARSING") return;
    if (!startTsRef.current) startTsRef.current = Date.now();
    const calc = () =>
      typeof backendStartTs === "number" && backendStartTs > 0
        ? Math.max(0, Math.floor(Date.now() / 1000 - backendStartTs))
        : Math.floor((Date.now() - startTsRef.current) / 1000);
    setElapsed(calc());
    elapsedRef.current = setInterval(() => setElapsed(calc()), 1000);
    return () => {
      clearInterval(elapsedRef.current);
      startTsRef.current = null;
      setElapsed(0);
    };
  }, [parseStatus, backendStartTs]);

  const poll = useCallback(async () => {
    if (!rfpId) return;
    try {
      const data = await v2Api.rfp.getProgress(rfpId);
      setSnapshot(data);
      const s = data?.progress?.stage;
      if (s && s !== "failed" && STEP_INDEX[s] !== undefined) setLastGoodStage(s);
      const newPartial = data?.partial_version ?? 0;
      if (newPartial > lastPartialRef.current) {
        lastPartialRef.current = newPartial;
        onPartial?.(data);
      }
      const status = (data?.parse_status || "").toUpperCase();
      if (TERMINAL_STATES.has(status)) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        onComplete?.(data);
      }
    } catch { /* transient — next tick retries */ }
  }, [rfpId, onComplete, onPartial]);

  useEffect(() => {
    if (parseStatus !== "PARSING") return;
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => { clearInterval(timerRef.current); timerRef.current = null; };
  }, [parseStatus, poll]);

  if (parseStatus !== "PARSING") return null;

  const progress  = snapshot?.progress;
  const stage     = progress?.stage || "parsing";
  const isFailed  = stage === "failed" || (snapshot?.parse_status || "").toUpperCase() === "FAILED";
  const activeIdx = isFailed ? (STEP_INDEX[lastGoodStage] ?? 0) : (STEP_INDEX[stage] ?? 0);
  const current   = progress?.current;
  const total     = progress?.total;
  const message   = progress?.message;
  const showFrac  = typeof total === "number" && total > 0 && typeof current === "number";
  const fmt       = (s) => s >= 60 ? `${Math.floor(s/60)}m ${s%60}s` : `${s}s`;

  return (
    <div className="border border-blue-200 bg-white rounded-xl p-4 shadow-sm w-full">
      {/* Title row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
          <span className="text-xs font-semibold text-slate-700">Analysing document</span>
        </div>
        <span className="text-[11px] text-slate-400 tabular-nums">{fmt(elapsed)}</span>
      </div>

      {/* Pipeline stepper */}
      <div className="flex items-start gap-0">
        {PIPELINE.map((step, idx) => {
          const Icon    = step.icon;
          const done    = idx < activeIdx;
          const active  = idx === activeIdx && !isFailed;
          const pending = idx > activeIdx;

          return (
            <React.Fragment key={step.key}>
              {/* Step node */}
              <div className="flex flex-col items-center flex-1 min-w-0">
                {/* Circle */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all ${
                  done                    ? "bg-emerald-500 text-white" :
                  active && !isFailed     ? "bg-blue-600 text-white ring-4 ring-blue-100" :
                  active && isFailed      ? "bg-rose-500 text-white ring-4 ring-rose-100" :
                                            "bg-slate-100 text-slate-400"
                }`}>
                  {done
                    ? <CheckCircle2 className="w-4 h-4" />
                    : active && !isFailed
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : active && isFailed
                        ? <AlertTriangle className="w-4 h-4" />
                        : <Icon className="w-3.5 h-3.5" />
                  }
                </div>

                {/* Label */}
                <p className={`mt-1.5 text-[10px] font-medium text-center leading-tight ${
                  done                 ? "text-emerald-600" :
                  active && !isFailed  ? "text-blue-700" :
                  active && isFailed   ? "text-rose-600" :
                                         "text-slate-400"
                }`}>{step.label}</p>

                {/* Active detail */}
                {active && (
                  <p className="mt-0.5 text-[9px] text-blue-500 text-center tabular-nums leading-tight">
                    {showFrac ? `${current}/${total}` : "…"}
                  </p>
                )}
              </div>

              {/* Connector line between steps */}
              {idx < PIPELINE.length - 1 && (
                <div className={`h-0.5 flex-1 mt-4 mx-1 rounded-full transition-all ${
                  idx < activeIdx ? "bg-emerald-400" : "bg-slate-200"
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Bottom message */}
      {(message || showFrac) && (
        <p className="mt-3 text-[11px] text-slate-500 text-center truncate">
          {message || (showFrac ? `Processing ${current} of ${total}…` : "")}
        </p>
      )}
    </div>
  );
}


export function ParseFailedPill({ error }) {
  if (!error) return null;
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-rose-300 bg-rose-50 text-rose-900 text-[11px]">
      <AlertTriangle className="w-3.5 h-3.5" />
      <span className="font-medium">Analyse failed:</span>
      <span className="truncate max-w-[280px]">{error}</span>
    </div>
  );
}
